import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BaseTable, features, useTablePipeline } from 'ali-react-table';
import { ConfigProvider, Card, Justify, Pagination } from 'tea-component';
import en_US from "tea-component/es/i18n/locale/en_US";
import zh_CN from "tea-component/es/i18n/locale/zh_CN";

import Loadings from '@/pages/dashboard/components/Loadings';
import NoData from '@/pages/dashboard/components/NoData';
import LoadingFailed from "@/pages/dashboard/components/LoadingFailed";
import SvgIcon from '@/pages/home/components/SvgIcon';
import HeatMapTableHeadCell from '@/pages/dashboard/components/HeatMapTable/HeatMapTableHeadCell';
import { closeActiveHeatMapBubble } from '@/pages/dashboard/components/HeatMapTable/_config/hooks';
import { useIntl, getLocale } from 'umi';
import { HeatMapTableProps } from '@/pages/dashboard/components/HeatMapTable/_config/interfaces';
import { handleTableCopyColumns } from '../../utils/tableCopy';
import { useOverLimitTip , OverLimitInlineTip, OverLimitBar } from '@/pages/dashboard/components/OverLimitTip';

import '@/pages/dashboard/components/OverLimitTip/index.scss';
import './HeatMapTable.scss';


function HeatMapTable(props: HeatMapTableProps) {

  const {
    records = [],
    columns: propColumns = [],
    sorting,
    className = '',
    defaultColumnWidth = 50,
    paging,
    loading = false,
    error = false,
    onDownload,
    title,
    topTip = {},
    height,
    timeType,
    copyData,
    copyConfig,
    frontLogicalFlag,
    limitCount,
    barLimitCount,
    barDownloadDisabled = true,
    overLimitBarTriggerKey,
    overLimitTipFormatRule,
  } = props;

  const recordsRef = useRef(records);

  const {isHaveWeek=true} = copyConfig|| {};

  useEffect(() => {
    recordsRef.current = copyData;
  }, [copyData]);

  // loading 切换前先关闭浮层，避免 cell 卸载时 portal 与 React 不同步
  useLayoutEffect(() => {
    if (loading) {
      closeActiveHeatMapBubble();
    }
  }, [loading]);

  useEffect(() => {
    return () => {
      closeActiveHeatMapBubble();
    };
  }, []);

  const { tipVisible } = useOverLimitTip(frontLogicalFlag);

  const [barDismissed, setBarDismissed] = useState(false);
  useEffect(() => {
    setBarDismissed(false);
  }, [overLimitBarTriggerKey]);
  useEffect(() => {
    if (frontLogicalFlag === 'has_more') {
      setBarDismissed(false);
    }
  }, [frontLogicalFlag]);
  const barVisible = tipVisible && !barDismissed;

  const { formatMessage } = useIntl();
  const locale = getLocale();

  const columns = useMemo(()=>{
    propColumns?.map(item=>{
      item.onChangeSorts = sorting?.onChangeSorts;
    });
    return propColumns;
  },[propColumns]);

  const handleTableCopyFunc = (type: string, key: string,colName:string) => {
    handleTableCopyColumns({ type, key, columns, records: recordsRef.current || records, selectType: 'value_only', formatMessage, timeType,colName,isHeatMapTable: true, isHaveWeek });
  };
  // 表格扩展机制
  const pipeline = useTablePipeline()
    .input({ dataSource: records, columns })
    .use(
      // 支持单列排序
      features.sort({
        mode: 'single',
        // defaultSorts: [],
        keepDataSource: true,
        SortHeaderCell: (props)=>HeatMapTableHeadCell(props,records,handleTableCopyFunc),
        ...sorting,
      }),
    )
    .use(
      // 支持拖拽调整列宽
      features.columnResize({
        fallbackSize: defaultColumnWidth,
        minSize: 130,
        maxSize: 500,
        handleBackground: 'transparent',
        handleHoverBackground: 'transparent',
        handleActiveBackground: 'transparent',
      }),
    );

  return (
    <>
      <Card className="register-table">
        {barVisible && (
          <OverLimitBar
            limitCount={limitCount}
            barLimitCount={barLimitCount}
            overLimitTipFormatRule={overLimitTipFormatRule}
            barDownloadDisabled={barDownloadDisabled}
            onDownload={() => onDownload?.()}
            onDismiss={() => setBarDismissed(true)}
          />
        )}
        <Card.Body
          title={<div className='table-title'>{title}</div>}
          operation={(typeof onDownload === 'function' && paging?.recordCount !== 0 && paging?.recordCount !== undefined) ? (
            <>
              {props.operation}

              <div
                className="register-table-flex"
              >
                <i onClick={() => onDownload()}><SvgIcon className='register-table-icon' type='download' /></i>
              </div>

            </>
          ) : ''}>

          <Justify
            left={topTip.left}
            right={
              <div className="register-table-colortext">
                {formatMessage({ id: 'engagement_retention_percentage' })}: 0 <span className='register-table-bgtext'></span> 100%
              </div>
            }
          />

          {loading ? <div className="table-empty" style={height ? { height } : {}}><Loadings /></div> :
            error ? <div className="table-empty" style={height ? { height } : {}}><LoadingFailed /></div> :
              paging?.recordCount == 0 ? <div className="table-empty" style={height ? { height } : {}}><NoData /></div> : (
                <>
                  <BaseTable

                    defaultColumnWidth={defaultColumnWidth}
                    className={`table-bgcolor-wrapper ${className}`}
                    {...pipeline.getProps()}
                  />
                  {
                    (paging && paging?.recordCount > 10) ?
                      <ConfigProvider locale={locale === 'en-US' ? en_US : zh_CN}>
                        <Pagination
                          className="table-pagination"
                          stateText={
                            paging.recordCount > 10 ? (
                              <div className="dbd-pagination-state">
                                <span className="dbd-pagination-state__records">
                                  {`${paging.recordCount} ${formatMessage({ id: 'common_records' })}`}
                                </span>
                                {tipVisible && (
                                  <>
                                    <span className="dbd-pagination-state__sep">|</span>
                                    <OverLimitInlineTip
                                      className="dbd-pagination-state__tip"
                                      limitCount={limitCount}
                                      overLimitTipFormatRule={overLimitTipFormatRule}
                                      onDownload={() => onDownload?.()}
                                      linkClassName="dbd-pagination-state__tip-link"
                                    />
                                  </>
                                )}
                              </div>
                            ) : ''
                          }
                          {...paging}
                        />
                      </ConfigProvider> : ''
                  }
                </>
              )
          }
        </Card.Body>
      </Card>
    </>
  );
}


// 导出组件
export default HeatMapTable;
